// src/components/reports/ReportsDashboard.tsx
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardHeader,
  Divider,
  Chip,
  Stack,
  alpha,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Category as CategoryIcon,
  Storefront as StorefrontIcon,
  ReceiptLong as ReceiptLongIcon,
  SupportAgent as SupportAgentIcon,
  QueryStats as QueryStatsIcon,
  Inventory2 as Inventory2Icon,
  ConfirmationNumber as ConfirmationNumberIcon,
  AccountBalanceWallet as WalletIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { DashboardInfoTip } from '../dashboard/components/DashboardInfoTip';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUI } from '../../providers/MoleUIContext';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { Util } from '../../util/Util';
import { allows, anyOf, pdfExport, reads, situation, type GrantSpec } from '../../util/access';
import DynamicReportFilterDialog, {
  DynamicFilterValues,
  ReportFilterFieldConfig,
} from '../../components/reporting/DynamicReportFilterDialog';
import InventoryPointReport from './inventory/InventoryPointReport';
import WorkspaceHub, { type HubSection } from '../hubs/WorkspaceHub';

interface ReportFilterConfig {
  title?: string;
  fields: ReportFilterFieldConfig[];
  allowEmptyFilters?: boolean;
  buildPayload: (values: DynamicFilterValues) => Record<string, unknown>;
}

interface ReportType {
  id: string;
  title: string;
  description: string;
  helpKey: string;
  icon: any;
  color: string;
  endpoint?: string;
  method?: 'GET' | 'POST';
  hasConfig: boolean;
  filterConfig?: ReportFilterConfig;
  /** Hide the card unless this grant is held (the backend enforces it too). */
  grant?: GrantSpec;
}

const buildLiveViewSections = (t: (key: string) => string): HubSection[] => [
  {
    id: 'credit',
    cards: [
      {
        id: 'invoice-situation',
        title: t('nav:tabs.invoice_situation'),
        description: t('common:hubs.cards.invoiceSituation'),
        path: '/invoice/situation',
        grant: situation('invoice'),
        color: '#1565c0',
        icon: ReceiptLongIcon,
      },
      {
        id: 'offers',
        title: t('nav:tabs.offers'),
        description: t('common:hubs.cards.offers'),
        path: '/offers',
        grant: reads('offer'),
        color: '#6a1b9a',
        icon: ReceiptLongIcon,
      },
    ],
  },
  {
    id: 'daily-sales',
    title: t('nav:tabs.sales'),
    cards: [
      { id: 'sales-item', title: t('nav:tabs.daily_sales_item'), description: t('common:hubs.cards.dailySales'), path: '/sales/item', grant: anyOf(reads('sale'), reads('return')), color: '#1976d2', icon: CategoryIcon },
      { id: 'sales-family', title: t('nav:tabs.daily_sales_family'), description: t('common:hubs.cards.dailySales'), path: '/sales/family', grant: anyOf(reads('sale'), reads('return')), color: '#1e88e5', icon: CategoryIcon },
      { id: 'sales-category', title: t('nav:tabs.daily_sales_category'), description: t('common:hubs.cards.dailySales'), path: '/sales/category', grant: anyOf(reads('sale'), reads('return')), color: '#42a5f5', icon: CategoryIcon },
      { id: 'sales-depot', title: t('nav:tabs.daily_sales_depot'), description: t('common:hubs.cards.dailySales'), path: '/sales/depot', grant: anyOf(reads('sale'), reads('return')), color: '#1565c0', icon: StorefrontIcon },
      { id: 'sales-sector', title: t('nav:tabs.daily_sales_sector'), description: t('common:hubs.cards.dailySales'), path: '/sales/sector', grant: anyOf(reads('sale'), reads('return')), color: '#0d47a1', icon: StorefrontIcon },
      { id: 'sales-user', title: t('nav:tabs.daily_sales_user'), description: t('common:hubs.cards.dailySales'), path: '/sales/user', grant: anyOf(reads('sale'), reads('return')), color: '#0277bd', icon: SupportAgentIcon },
    ],
  },
  {
    id: 'daily-payments',
    title: t('nav:tabs.payments'),
    cards: [
      { id: 'pay-mode', title: t('nav:tabs.daily_payments_mode'), description: t('common:hubs.cards.dailyPayments'), path: '/payments/mode', grant: reads('payment'), color: '#2e7d32', icon: PaymentsIcon },
      { id: 'pay-type', title: t('nav:tabs.daily_payments_type'), description: t('common:hubs.cards.dailyPayments'), path: '/payments/type', grant: reads('payment'), color: '#388e3c', icon: PaymentsIcon },
      { id: 'pay-user', title: t('nav:tabs.daily_payments_user'), description: t('common:hubs.cards.dailyPayments'), path: '/payments/user', grant: reads('payment'), color: '#43a047', icon: SupportAgentIcon },
      { id: 'pay-depot', title: t('nav:tabs.daily_payments_depot'), description: t('common:hubs.cards.dailyPayments'), path: '/payments/depot', grant: reads('payment'), color: '#66bb6a', icon: StorefrontIcon },
    ],
  },
  {
    id: 'daily-supply-payments',
    title: t('nav:tabs.supply_payments'),
    cards: [
      { id: 'sp-mode', title: t('nav:tabs.daily_supply_payments_mode'), description: t('common:hubs.cards.dailyPayments'), path: '/supply-payments/mode', grant: reads('supply_payment'), color: '#6a1b9a', icon: PaymentsIcon },
      { id: 'sp-user', title: t('nav:tabs.daily_supply_payments_user'), description: t('common:hubs.cards.dailyPayments'), path: '/supply-payments/user', grant: reads('supply_payment'), color: '#8e24aa', icon: SupportAgentIcon },
      { id: 'sp-depot', title: t('nav:tabs.daily_supply_payments_depot'), description: t('common:hubs.cards.dailyPayments'), path: '/supply-payments/depot', grant: reads('supply_payment'), color: '#ab47bc', icon: StorefrontIcon },
    ],
  },
];

const buildReportTypes = (t: (key: string) => string): ReportType[] => [
  {
    id: 'sales-category',
    title: t('common:report.types.salesCategory.title'),
    description: t('common:report.types.salesCategory.description'),
    helpKey: 'salesCategory',
    icon: CategoryIcon,
    color: '#1976d2',
    hasConfig: true,
    grant: pdfExport('report'),
    endpoint: '/reports/sales/category',
    filterConfig: {
      fields: [{ type: 'date-range', required: true }],
      buildPayload: (values) => ({
        From: Util.formatISODate(values.dateRange.from as Date),
        To: Util.formatISODate(values.dateRange.to as Date),
      }),
    },
  },
  {
    id: 'sales-client',
    title: t('common:report.types.salesClientCategory.title'),
    description: t('common:report.types.salesClientCategory.description'),
    helpKey: 'salesClientCategory',
    icon: StorefrontIcon,
    color: '#6a1b9a',
    hasConfig: true,
    grant: pdfExport('report'),
    endpoint: '/reports/sales/client/category',
    filterConfig: {
      fields: [
        { type: 'clients', required: false, listFormat: 'array' },
        { type: 'date-range', required: true },
      ],
      buildPayload: (values) => ({
        Clients: values.clientIds,
        From: Util.formatISODate(values.dateRange.from as Date),
        To: Util.formatISODate(values.dateRange.to as Date),
      }),
    },
  },
  {
    id: 'invoice-ledger-client',
    title: t('common:report.types.clientInvoiceLedger.title'),
    description: t('common:report.types.clientInvoiceLedger.description'),
    helpKey: 'clientInvoiceLedger',
    icon: ReceiptLongIcon,
    color: '#1565c0',
    hasConfig: true,
    grant: pdfExport('report'),
    endpoint: '/reports/invoices/clients/ledger',
    filterConfig: {
      fields: [
        { type: 'clients', required: false, listFormat: 'array' },
        { type: 'date-range', required: true },
        { type: 'orientation' },
      ],
      buildPayload: (values) => ({
        Clients: values.clientIds,
        From: Util.formatISODate(values.dateRange.from as Date),
        To: Util.formatISODate(values.dateRange.to as Date),
        orientation: values.orientation,
      }),
    },
  },
  {
    id: 'invoice-ledger-agent',
    title: t('common:report.types.agentInvoiceLedger.title'),
    description: t('common:report.types.agentInvoiceLedger.description'),
    helpKey: 'agentInvoiceLedger',
    icon: SupportAgentIcon,
    color: '#ef6c00',
    hasConfig: true,
    grant: pdfExport('report'),
    endpoint: '/reports/invoices/agents/ledger',
    filterConfig: {
      fields: [
        { type: 'agents', required: false, listFormat: 'array' },
        { type: 'date-range', required: true },
        { type: 'orientation' },
      ],
      buildPayload: (values) => ({
        Agents: values.agentIds,
        From: Util.formatISODate(values.dateRange.from as Date),
        To: Util.formatISODate(values.dateRange.to as Date),
        orientation: values.orientation,
      }),
    },
  },
  {
    id: 'invoice-ledger-supplier',
    title: t('common:report.types.supplierInvoiceLedger.title'),
    description: t('common:report.types.supplierInvoiceLedger.description'),
    helpKey: 'supplierInvoiceLedger',
    icon: StorefrontIcon,
    color: '#6a1b9a',
    hasConfig: true,
    grant: pdfExport('report'),
    endpoint: '/reports/supplies/suppliers/ledger',
    filterConfig: {
      fields: [
        { type: 'suppliers', required: false, listFormat: 'array' },
        { type: 'date-range', required: true },
      ],
      buildPayload: (values) => ({
        Suppliers: values.supplierIds,
        From: Util.formatISODate(values.dateRange.from as Date),
        To: Util.formatISODate(values.dateRange.to as Date),
      }),
    },
  },
  {
    id: 'client-statistics',
    title: t('common:report.types.clientStatistics.title'),
    description: t('common:report.types.clientStatistics.description'),
    helpKey: 'clientStatistics',
    icon: QueryStatsIcon,
    color: '#00796b',
    hasConfig: true,
    method: 'GET',
    grant: situation('invoice'),
    endpoint: '/print/statistics/clients',
    filterConfig: {
      fields: [{ type: 'date', required: true }],
      buildPayload: (values) => ({
        date: Util.formatISODate(values.date as Date),
      }),
    },
  },
  {
    id: 'vouchers',
    title: t('common:report.types.vouchers.title'),
    description: t('common:report.types.vouchers.description'),
    helpKey: 'vouchers',
    icon: ConfirmationNumberIcon,
    color: '#c62828',
    hasConfig: true,
    grant: pdfExport('voucher'),
    endpoint: '/reports/vouchers',
    filterConfig: {
      fields: [{ type: 'date-range', required: true }],
      buildPayload: (values) => ({
        From: Util.formatISODate(values.dateRange.from as Date),
        To: Util.formatISODate(values.dateRange.to as Date),
      }),
    },
  },
  {
    id: 'expenses',
    title: t('common:report.types.expenses.title'),
    description: t('common:report.types.expenses.description'),
    helpKey: 'expenses',
    icon: PaymentsIcon,
    color: '#6d4c41',
    hasConfig: true,
    grant: pdfExport('expense'),
    endpoint: '/reports/expenses',
    filterConfig: {
      fields: [{ type: 'date-range', required: true }],
      buildPayload: (values) => ({
        From: Util.formatISODate(values.dateRange.from as Date),
        To: Util.formatISODate(values.dateRange.to as Date),
      }),
    },
  },
  {
    id: 'wallet-records',
    title: t('common:report.types.walletRecords.title'),
    description: t('common:report.types.walletRecords.description'),
    helpKey: 'walletRecords',
    icon: WalletIcon,
    color: '#1565c0',
    hasConfig: true,
    grant: pdfExport('wallet'),
    endpoint: '/reports/wallets/records',
    filterConfig: {
      fields: [{ type: 'date-range', required: true }],
      buildPayload: (values) => ({
        From: Util.formatISODate(values.dateRange.from as Date),
        To: Util.formatISODate(values.dateRange.to as Date),
      }),
    },
  },
  {
    id: 'inventory-point',
    title: t('common:report.types.inventoryView.title'),
    description: t('common:report.types.inventoryView.description'),
    helpKey: 'inventoryView',
    icon: Inventory2Icon,
    color: '#2e7d32',
    hasConfig: true,
    grant: reads('inventory_current'),
    filterConfig: {
      fields: [
        { type: 'depot', required: true },
        { type: 'inventory-type', required: true },
        { type: 'date', required: true },
      ],
      buildPayload: (values) => ({
        Depot: values.depotId,
        InvType: values.inventoryType,
        Date: Util.formatISODate(values.date as Date),
      }),
    },
  },
  // {
  //   id: 'inventory-status',
  //   title: 'Stock Status Report',
  //   description: 'Current stock levels, low stock & expiry alerts',
  //   icon: InventoryIcon,
  //   color: '#388e3c',
  //   hasConfig: true,
  // },
  // {
  //   id: 'purchase-history',
  //   title: 'Purchase History',
  //   description: 'Supplier purchases, costs & trends',
  //   icon: PurchasesIcon,
  //   color: '#f57c00',
  //   hasConfig: true,
  // },
  // {
  //   id: 'conversion-report',
  //   title: 'Item Conversion / Kit Report',
  //   description: 'Kit assembly, de-kit and swap operations',
  //   icon: ConversionIcon,
  //   color: '#7b1fa2',
  //   hasConfig: false, // we already have modal version
  // },
  // {
  //   id: 'profit-loss',
  //   title: 'Profit & Loss',
  //   description: 'Revenue, cost of sales & gross profit',
  //   icon: ProfitIcon,
  //   color: '#d32f2f',
  //   hasConfig: true,
  // },
  // {
  //   id: 'customer-performance',
  //   title: 'Customer Performance',
  //   description: 'Top customers, purchase frequency & value',
  //   icon: CustomersIcon,
  //   color: '#0288d1',
  //   hasConfig: true,
  // },
];

export default function ReportsDashboard() {
  const navigate = useNavigate();
  const [loadingReport, setLoadingReport] = useState<string | null>(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState<boolean>(false);
  const [inventoryFilters, setInventoryFilters] = useState<any>(null);


  const { t } = useTranslation();
  const reportTypes = buildReportTypes(t);
  const liveSections = buildLiveViewSections(t);
  const { api, session, access } = useAuth();
  const principal = { access };
  const hasLiveViews = liveSections.some((section) =>
    section.cards.some((card) => allows(principal, card.grant)),
  );
  const { setLoadingMessage, queueReRun } = useUI();

  const selectedReportRef = useRef<ReportType | null>(null);

  const reportTitles: Record<string, string> = {
    'sales-category': t('common:report.types.salesCategory.title'),
    'sales-client': t('common:report.types.salesClientCategory.title'),
    'invoice-ledger-client': t('common:report.types.clientInvoiceLedger.title'),
    'invoice-ledger-agent': t('common:report.types.agentInvoiceLedger.title'),
    'client-statistics': t('common:report.types.clientStatistics.title'),
    'inventory-point': t('common:report.types.inventoryView.title'),
    'vouchers': t('common:report.types.vouchers.title'),
    'expenses': t('common:report.types.expenses.title'),
    'wallet-records': t('common:report.types.walletRecords.title'),
  };

  const reportDescriptions: Record<string, string> = {
    'sales-category': t('common:report.types.salesCategory.description'),
    'sales-client': t('common:report.types.salesClientCategory.description'),
    'invoice-ledger-client': t('common:report.types.clientInvoiceLedger.description'),
    'invoice-ledger-agent': t('common:report.types.agentInvoiceLedger.description'),
    'client-statistics': t('common:report.types.clientStatistics.description'),
    'inventory-point': t('common:report.types.inventoryView.description'),
    'vouchers': t('common:report.types.vouchers.description'),
    'expenses': t('common:report.types.expenses.description'),
    'wallet-records': t('common:report.types.walletRecords.description'),
  };

  // Dialog titles may differ from card titles (e.g. inventory-point shows "Stock Trace Dialog")
  const filterDialogTitles: Record<string, string> = {
    'sales-category': t('common:report.types.salesCategory.title'),
    'sales-client': t('common:report.types.salesClientCategory.title'),
    'invoice-ledger-client': t('common:report.types.clientInvoiceLedger.title'),
    'invoice-ledger-agent': t('common:report.types.agentInvoiceLedger.title'),
    'client-statistics': t('common:dialog.statisticsDate'),
    'inventory-point': t('common:report.stockTraceDialog'),
    'vouchers': t('common:report.types.vouchers.title'),
    'expenses': t('common:report.types.expenses.title'),
    'wallet-records': t('common:report.types.walletRecords.title'),
  };

  useEffect(() => {
    setInventoryFilters(null);
  }, []);

  const openPdfBlob = (encoded: string) => {
    const pdfData = atob(encoded);
    const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], { type: 'application/pdf' });
    window.open(URL.createObjectURL(blob));
  };

  const exportFilteredReport = async (report: ReportType, filters: Record<string, unknown>) => {
    if (!report.endpoint) return;
    if (report.grant && !allows(principal, report.grant)) return;

    setLoadingMessage('EXPORT');

    const method = report.method ?? 'POST';
    const res = method === 'GET'
      ? await api.get(`${report.endpoint}/${filters.date}`)
      : await api.post(report.endpoint, filters);

    if (res?.status === 200) {
      openPdfBlob(res.data.data);
    } else if (res) {
      console.error('Failed to fetch data:', res);
    }

    setLoadingMessage(null);
  };

  const handleReportClick = (report: ReportType) => {
    selectedReportRef.current = report;
    if (report.hasConfig && report.filterConfig) {
      setIsFilterDialogOpen(true);
    } else {
      // Direct download or open existing modal
      if (report.id === 'conversion-report') {
        // If you want to keep the existing modal approach
        // You would normally open modal here with batch selection etc.
        toast.info(t('common:report.selectConversionBatch'));
        navigate('/conversions'); // or wherever your list is
      } else {
        handleDirectDownload(report.id);
      }
    }
  };

  const handleApplyFilters = async (values: DynamicFilterValues) => {
    const selectedReport = selectedReportRef.current;
    if (!selectedReport || !selectedReport.filterConfig) return;

    const payload = selectedReport.filterConfig.buildPayload(values);

    if (selectedReport.id === 'inventory-point') {
      setInventoryFilters(payload);
      return;
    }

    await exportFilteredReport(selectedReport, payload);
  };

  // Fallback for a report that has no filterConfig. There is no generic
  // /reports/<id>/pdf endpoint -- every report is exported through its own
  // filterConfig.buildPayload() path -- so there is nothing to download here.
  // This previously slept 1.8s and then showed a success toast, telling the user
  // a file had been produced when none had. Fail visibly instead.
  const handleDirectDownload = (reportId: string) => {
    console.error(`No export configured for report "${reportId}"`);
    toast.error(t('common:report.failedToGenerate'));
    setLoadingReport(null);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 1400,
        mx: 'auto',
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <div>
          <Typography variant="h4" fontWeight={600} component="h1">
            {t('common:report.reportsCenter')}
          </Typography>
          <Typography color="text.secondary">
            {t('common:report.reportsSubtitle')} • {new Date().toLocaleDateString()}
          </Typography>
        </div>

        <Chip
          icon={<PdfIcon />}
          label={t('common:report.allReportsPdf')}
          color="primary"
          variant="outlined"
        />
      </Stack>

      <Divider sx={{ mb: 4 }} />

      {hasLiveViews && (
        <>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
            {t('common:hubs.liveViews')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('common:hubs.liveViewsHint')}
          </Typography>
          <WorkspaceHub hideChrome sections={liveSections} />
          <Divider sx={{ my: 4 }} />
        </>
      )}

      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        {t('common:report.reportsCenter')}
      </Typography>

      <Grid container spacing={3}>
        {reportTypes
          .filter((report) => !report.grant || allows(principal, report.grant))
          .map((report) => {
          const Icon = report.icon;
          const isLoading = loadingReport === report.id;

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={report.id}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'all 0.25s',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: 8,
                  },
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: alpha(report.color, 0.2),
                }}
              >
                <CardActionArea
                  onClick={() => handleReportClick(report)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  disabled={isLoading}
                >
                  <CardHeader
                    avatar={
                      <Box
                        sx={{
                          bgcolor: alpha(report.color, 0.12),
                          color: report.color,
                          borderRadius: '50%',
                          width: 56,
                          height: 56,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon fontSize="large" />
                      </Box>
                    }
                    title={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pr: 1 }}>
                        <Typography variant="h6" component="span" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {reportTitles[report.id] || report.title}
                        </Typography>
                        <DashboardInfoTip helpKey={report.helpKey} scope="report" />
                      </Box>
                    )}
                    subheader={reportDescriptions[report.id] || report.description}
                    subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    sx={{ pb: 1 }}
                  />

                  <CardContent sx={{ flexGrow: 1, pt: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mt={2}>
                      {report.hasConfig ? (
                        <>
                          <Chip
                            size="small"
                            label={t('common:ui.buttons.configure')}
                            color="primary"
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {report.id === 'client-statistics'
                              ? t('common:dialog.statisticsDate')
                              : t('common:report.dateRangeFiltersFormat')}
                          </Typography>
                        </>
                      ) : (
                        <Chip
                          size="small"
                          icon={<DownloadIcon fontSize="small" />}
                          label={t('common:ui.buttons.quickDownload')}
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>


      {
        selectedReportRef.current?.filterConfig && (
          <DynamicReportFilterDialog
            open={isFilterDialogOpen}
            onClose={() => setIsFilterDialogOpen(false)}
            title={filterDialogTitles[selectedReportRef.current.id] ?? selectedReportRef.current.title}
            fields={selectedReportRef.current.filterConfig.fields}
            allowEmptyFilters={selectedReportRef.current.filterConfig.allowEmptyFilters}
            onApply={handleApplyFilters}
          />
        )
      }

      {
        inventoryFilters && (
          <InventoryPointReport
            isOpen={!!inventoryFilters}
            toggle={() => setInventoryFilters(null)}
            bundle={inventoryFilters}
          />
        )
      }

      <Box sx={{ mt: 6, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="caption">
          {t('common:report.footerCaption', { year: new Date().getFullYear() })}
        </Typography>
      </Box>
    </Box >
  );
}